import { useState } from 'react';

import { Autocomplete } from '@/components/Inputs/Autocomplete';
import { TextArea } from '@/components/Inputs/TextArea';
import SelectionChip from '@/components/SelectionChip/SelectionChip';
import {
  type DietAnswer,
  dietGroup,
  type DietGroupId,
  dietLabel,
  type DietSeverity,
  searchDietRegister,
  useDietRegister,
} from '@/store/persons/allergiesDiet';

import s from './AllergiesDietPicker.module.scss';
import { dietChipVariant, dietGlyphIcon } from './dietGlyph';

const SEVERITY_LABEL: Record<DietSeverity, string> = {
  life_threatening: 'Life-threatening',
  medical: 'Medical',
  strict: 'Strict',
  preference: 'Preference',
};

/** `dietary_restrictions` holds each entry in up to 255 characters. */
const OTHER_MAX_LENGTH = 255;

/* Both notes share `allergies` with the picks, and that field holds 2000 characters. */
const NOTE_MAX_LENGTH = 500;

type AllergiesDietPickerProps = {
  group: DietGroupId;
  value: DietAnswer;
  onChange: (next: DietAnswer) => void;
};

/**
 * One group of allergies and dietary exclusions, as the guest answers it in onboarding and
 * edits it in My Profile: the common picks as toggles, the register behind a search for
 * anything else, free text for what the register lacks, and a note for the chef.
 */
export const AllergiesDietPicker = ({ group, value, onChange }: AllergiesDietPickerProps) => {
  /* The search waits for the register; the common picks do not. */
  const register = useDietRegister();
  const [query, setQuery] = useState('');
  const g = dietGroup(group);
  const variant = dietChipVariant(group);

  const toggle = (code: string) =>
    onChange({
      ...value,
      codes: value.codes.includes(code)
        ? value.codes.filter((c) => c !== code)
        : [...value.codes, code],
    });

  const addOther = (typed: string) => {
    const text = typed.trim().slice(0, OTHER_MAX_LENGTH);
    setQuery('');
    if (!text || value.other.some((o) => o.toLowerCase() === text.toLowerCase())) return;
    onChange({ ...value, other: [...value.other, text] });
  };

  /* Picks found by search stay in the row after the common ones, so they can be
     unpicked the same way. */
  const searched = value.codes.filter((code) => !g.quick.some((pick) => pick.code === code));
  const results = register ? searchDietRegister(register, group, query) : [];

  return (
    <div className={s.root}>
      <div className={s.pills}>
        {[...g.quick.map((pick) => pick.code), ...searched].map((code) => (
          <SelectionChip
            key={code}
            label={dietLabel(code, register) ?? code}
            icon={dietGlyphIcon(group, code)}
            variant={variant}
            size="lg"
            selected={value.codes.includes(code)}
            onToggle={() => toggle(code)}
          />
        ))}
        {value.other.map((text) => (
          <SelectionChip
            key={`other:${text}`}
            label={text}
            variant={variant}
            size="lg"
            onRemove={() => onChange({ ...value, other: value.other.filter((o) => o !== text) })}
          />
        ))}
      </div>

      <div className={s.search}>
        <Autocomplete<string>
          label="Not on the list?"
          placeholder={g.searchPlaceholder}
          value={query}
          onChange={setQuery}
          options={results.map((entry) => {
            const added = value.codes.includes(entry.code);
            return {
              value: entry.code,
              disabled: added,
              label: (
                <span className={s.result}>
                  <span className={s.resultName}>{dietLabel(entry.code, register)}</span>
                  <span className={s.resultMeta}>
                    {added ? 'Added' : SEVERITY_LABEL[entry.severity]}
                  </span>
                </span>
              ),
            };
          })}
          onItemSelect={(code) => {
            if (code && !value.codes.includes(code))
              onChange({ ...value, codes: [...value.codes, code] });
            setQuery('');
          }}
          onNewOption={addOther}
          newOptionLabel={(typed) => <>Add “{typed.trim()}” as other</>}
        />
        {register && (
          <small className={s.hint}>
            Searches {register[group].length} items from the VesselHaven dietary register.
          </small>
        )}
      </div>

      <TextArea
        label="Anything else the chef should know? (optional)"
        rows={3}
        maxLength={NOTE_MAX_LENGTH}
        placeholder={g.notePlaceholder}
        value={value.note}
        onChange={(e) => onChange({ ...value, note: e.target.value })}
      />
    </div>
  );
};
